/**
 * Los botones del asistente: a qué sitio EXACTO de la app puede llevarte.
 *
 * FUENTE ÚNICA. La edge function `ipm-ai-assistant` corre en Deno y no puede
 * importar nada de `src/`, así que `scripts/sync-assistant-actions.mjs` copia
 * este fichero tal cual a `supabase/functions/ipm-ai-assistant/assistantActions.generated.ts`
 * (gate `npm run assistant:actions:check`, igual que el de etiquetas). Por eso
 * es PURO a propósito: cero imports, solo datos y funciones sin efectos. Un
 * import aquí rompe la copia y el script lo rechaza.
 *
 * POR QUÉ EXISTE. Hasta ahora el chat solo sabía mandarte a la PÁGINA del tema
 * («Equipo») y tú buscabas el botón. La pregunta real es «¿cómo le cambio la
 * contraseña a Amadou?», y la respuesta útil es un botón que abre la ficha de
 * Amadou en el paso de Acceso. Cada destino de aquí es una ruta del router más
 * unos parámetros que la página correspondiente lee UNA vez al montar (y
 * limpia de la URL): así un enlace del asistente abre el diálogo exacto.
 *
 * TRES REGLAS que vigila src/__tests__/assistantActions.test.ts:
 *  1. Toda `path` está montada en el router y NO redirige (una redirección se
 *     come la `?` o te deja en otra pantalla).
 *  2. Todo `query.action` (y los `params` de entidad) tiene quien lo lea en el
 *     fichero `reader`.
 *  3. Un id que va en la URL SOLO lo pone el servidor, resuelto por NOMBRE bajo
 *     RLS (`open_in_app`). El modelo nunca escribe un uuid: `buildActionUrl`
 *     recibe los ids aparte de los valores libres y rechaza cualquier valor
 *     libre con pinta de uuid.
 */

export type AssistantActionRole = "admin" | "manager";
export type AssistantActionEntity = "member" | "product" | "task";
export type AssistantActionPanel = "taskVoice" | "scoutingVoice";
export type AssistantValueArg = "date_from" | "date_to" | "mode";

export interface AssistantAction {
  /** Identificador estable. Es lo que el modelo pasa a `open_in_app`. */
  id: string;
  /** Ruta del router, sin query. Con `:id` si la entidad va en el camino. */
  path: string;
  /** Parámetros fijos de la URL. */
  query?: Record<string, string>;
  /**
   * La cosa concreta a la que lleva (una persona, un producto, una tarea). La
   * resuelve el SERVIDOR a partir del nombre que dijo el usuario; `param` es
   * dónde va su id: `:id` (en el camino) o el nombre de un parámetro.
   * `value: "key"` = lo que va en la URL no es el id sino la CLAVE de nombre que
   * usa la pantalla (el filtro de producto del informe de costes agrupa por el
   * nombre normalizado, no por id). También la pone el servidor, nunca el modelo.
   */
  entity?: { kind: AssistantActionEntity; param: string; value?: "id" | "key" };
  /**
   * Valores libres que sí puede dar el modelo: fechas YYYY-MM-DD (de verdad:
   * «2026-02-31» no vale) o un valor de una lista cerrada (`oneOf`). Si llega
   * alguno, se añade también `queryWhenValues` (p. ej. preset=custom).
   */
  values?: Array<{ param: string; arg: AssistantValueArg; oneOf?: string[] }>;
  /** Estos valores van juntos o no van (un rango con una sola punta no es un rango). */
  valuesTogether?: AssistantValueArg[];
  queryWhenValues?: Record<string, string>;
  /** Quién ve el botón. Mismo reparto que la pantalla de destino. */
  roles: AssistantActionRole[];
  /**
   * Sección que un ENCARGADO tiene que tener visible (clave de
   * MANAGEABLE_SECTIONS en useManagerPermissions). Si el admin se la ocultó,
   * el botón no se enseña: la ruta le rebotaría.
   */
  managerSection?: string;
  /** Sub-permiso que un encargado tiene que tener (SUB_PERMISSIONS). */
  managerSubPermission?: string;
  /** Clave i18n de la etiqueta. La etiqueta es la del botón que ve en pantalla. */
  labelKey: string;
  /** Abre un panel del micrófono en vez de navegar. */
  panel?: AssistantActionPanel;
  /** Fichero que lee los parámetros (para el test de lectores). */
  reader?: string;
  /** Una línea en inglés para el modelo: qué abre. */
  hint: string;
}

/**
 * Filtros del informe de costes que el asistente puede dejar puestos. `mode`
 * son los valores de la pantalla (CostesReport: biological / chemical /
 * unclassified); «sin clasificar» no se ofrece, nadie lo pide.
 */
const COSTS_VALUES: AssistantAction["values"] = [
  { param: "from", arg: "date_from" },
  { param: "to", arg: "date_to" },
  { param: "mode", arg: "mode", oneOf: ["biological", "chemical"] },
];

export const ASSISTANT_ACTIONS: AssistantAction[] = [
  /* ── Equipo ─────────────────────────────────────────────────────────── */
  {
    id: "team_add_member",
    path: "/settings/team",
    query: { action: "add-member" },
    roles: ["admin", "manager"],
    labelKey: "settings.team.addMember",
    reader: "src/pages/SettingsTeam.tsx",
    hint: "the Add member form",
  },
  {
    id: "team_member_edit",
    path: "/settings/team",
    query: { step: "identity" },
    entity: { kind: "member", param: "edit" },
    /* Solo admin: el servidor (update-team-member-profile) rechaza cualquier
       cambio de un encargado que no sea la foto, y el diálogo manda siempre el
       nombre, así que a un encargado este botón le llevaría a un «guardar» que
       falla. */
    roles: ["admin"],
    labelKey: "settings.team.editMember",
    reader: "src/pages/SettingsTeam.tsx",
    hint: "a member's edit form: name, photo, username (admin only)",
  },
  {
    id: "team_member_password",
    path: "/settings/team",
    query: { step: "access" },
    entity: { kind: "member", param: "edit" },
    roles: ["admin"],
    labelKey: "settings.team.changePassword",
    reader: "src/pages/SettingsTeam.tsx",
    hint: "a member's Access step, to change their password or email (admin only)",
  },
  {
    id: "team_member_scope",
    path: "/settings/team",
    query: { step: "scope" },
    entity: { kind: "member", param: "edit" },
    roles: ["admin"],
    labelKey: "settings.team.stepRoleAndScope",
    reader: "src/pages/SettingsTeam.tsx",
    hint: "a member's Role and farms step: change role or limit farms (admin only)",
  },
  {
    id: "team_member_permissions",
    path: "/settings/team",
    query: { step: "permissions" },
    entity: { kind: "member", param: "edit" },
    roles: ["admin"],
    labelKey: "settings.team.stepPermissions",
    reader: "src/pages/SettingsTeam.tsx",
    hint: "a manager's Permissions step: hide sections or prices (admin only)",
  },
  {
    id: "team_member_sectors",
    path: "/teams",
    entity: { kind: "member", param: "member" },
    roles: ["admin", "manager"],
    managerSection: "teams",
    labelKey: "teams.assignedSections",
    reader: "src/pages/Teams.tsx",
    hint: "a worker's card in Team management, where their sectors are assigned",
  },
  {
    id: "team_perito_invite",
    path: "/settings/team",
    query: { action: "perito" },
    roles: ["admin", "manager"],
    labelKey: "perito.multiCompany.generateInvite",
    reader: "src/pages/SettingsTeam.tsx",
    hint: "the card that links an external perito",
  },
  {
    id: "team_page",
    path: "/settings/team",
    roles: ["admin", "manager"],
    labelKey: "settings.team.title",
    hint: "Team and roles",
  },
  {
    id: "teams_page",
    path: "/teams",
    roles: ["admin", "manager"],
    managerSection: "teams",
    labelKey: "teams.title",
    hint: "Team management: each worker's tasks and sectors",
  },

  /* ── Cuenta propia y ajustes ────────────────────────────────────────── */
  {
    id: "my_account_edit_profile",
    path: "/settings/my-account",
    query: { action: "edit-profile" },
    roles: ["admin", "manager"],
    labelKey: "settings.editProfile",
    reader: "src/pages/SettingsMyAccount.tsx",
    hint: "your own Edit profile form (name, email, password)",
  },
  {
    id: "my_account",
    path: "/settings/my-account",
    roles: ["admin", "manager"],
    labelKey: "settings.myAccount",
    hint: "My account",
  },
  {
    id: "settings_regional",
    path: "/settings/regional",
    roles: ["admin", "manager"],
    labelKey: "settings.regional.title",
    hint: "language, currency and number format",
  },
  {
    id: "settings_account",
    path: "/settings/account",
    roles: ["admin", "manager"],
    labelKey: "settings.account.title",
    hint: "linked Google/Apple sign-in and (admin) the company data export",
  },
  {
    id: "settings_billing",
    path: "/settings/billing",
    /* SOLO ADMIN (usuario 24-09-2026): el plan de la empresa no se concede. */
    roles: ["admin"],
    labelKey: "settings.billingAndPlan",
    hint: "plan, subscription and invoices",
  },
  {
    id: "settings_notifications",
    path: "/settings/reports",
    roles: ["admin", "manager"],
    labelKey: "settings.notifications.title",
    hint: "which alerts you get, and the weekly summary email",
  },
  {
    id: "settings_company",
    path: "/settings/company",
    roles: ["admin"],
    labelKey: "settings.company.title",
    hint: "company profile and the names of your own levels",
  },
  {
    /* 27-09-2026: lo que el cuaderno de campo pide una vez tiene su página. La ven
       admin (edita) y encargado (solo lee), como la página. Los botones del modo
       cuaderno le añaden `?seccion=…` (rutaAjustesCuaderno, contrato con la página). */
    id: "settings_cuaderno",
    path: "/settings/cuaderno",
    roles: ["admin", "manager"],
    labelKey: "cuadernoAjustes.title",
    hint: "the field notebook's one-time data: holding, holder, advisor, crop defaults, machinery, licences, service companies and SIGPAC",
  },
  {
    /* 29-09-2026 (Personalizar informes): logo, colores y datos de la cabecera de
       los informes. Solo el admin (SoloAdminGuard). `mode` abre un apartado
       (`?seccion=`): cabecera = «Datos de la cabecera» (el chip «Falta NIF · Añadir»
       de un informe del chat). */
    id: "settings_marca",
    path: "/settings/marca",
    roles: ["admin"],
    values: [{ param: "seccion", arg: "mode", oneOf: ["escanear", "logo", "colores", "cabecera", "prueba"] }],
    reader: "src/pages/SettingsMarca.tsx",
    labelKey: "marcaInformes.title",
    hint: "the company's report branding: logo, colours and the company data in the report header (mode: escanear, logo, colores, cabecera, prueba) — admin only",
  },
  {
    id: "settings_structure",
    path: "/settings/structure",
    roles: ["admin"],
    labelKey: "settings.structure.title",
    hint: "locations, greenhouses and sectors (admin only)",
  },
  {
    id: "settings_campaigns",
    path: "/settings/campaigns",
    roles: ["admin", "manager"],
    managerSection: "campaigns",
    labelKey: "nav.campaigns",
    hint: "campaigns",
  },
  {
    id: "help_center",
    path: "/tutorials",
    roles: ["admin", "manager"],
    labelKey: "tutorials.nav",
    hint: "the Help Center and what is still missing in the setup",
  },

  /* ── Voz ────────────────────────────────────────────────────────────── */
  {
    id: "voice_task",
    path: "/planner",
    panel: "taskVoice",
    roles: ["admin", "manager"],
    labelKey: "voiceModal.headerTask",
    hint: "the microphone, on the voice Task tab",
  },
  {
    id: "voice_scouting",
    path: "/pests",
    panel: "scoutingVoice",
    roles: ["admin", "manager"],
    labelKey: "worker.incident.reportTitle",
    hint: "the microphone, on the scouting (report a pest) tab",
  },

  /* ── Planificador ───────────────────────────────────────────────────── */
  {
    id: "planner_new_task",
    path: "/planner",
    query: { action: "new-task" },
    roles: ["admin", "manager"],
    managerSection: "planner",
    labelKey: "planner.newTask",
    reader: "src/pages/Planner.tsx",
    hint: "the New task form",
  },
  {
    id: "planner_open_task",
    path: "/planner",
    entity: { kind: "task", param: "taskId" },
    roles: ["admin", "manager"],
    managerSection: "planner",
    labelKey: "nav.planner",
    reader: "src/pages/Planner.tsx",
    hint: "one task's detail (edit, complete, mark pending, delete)",
  },
  {
    id: "planner",
    path: "/planner",
    roles: ["admin", "manager"],
    managerSection: "planner",
    labelKey: "nav.planner",
    hint: "the planner calendar",
  },

  /* ── Registros de aplicación ────────────────────────────────────────── */
  {
    id: "spray_log_new",
    path: "/spray-logs",
    query: { action: "new" },
    roles: ["admin", "manager"],
    managerSection: "sprayLogs",
    labelKey: "sprayLogs.newLogEntry",
    reader: "src/pages/SprayLogs.tsx",
    hint: "the new application record form (for past treatments that must not touch stock, use spray_log_historic)",
  },
  {
    /* El mismo alta con «Registro histórico» ya encendido. Es la respuesta a
       «¿cómo meto tratamientos antiguos sin descontar stock?»: con el botón
       genérico había que acordarse de encender el interruptor, y el fallo típico
       era olvidarlo y descontar del stock de HOY. El formulario solo lo aplica a
       un alta y a admin/encargado (los mismos roles que aquí). */
    id: "spray_log_historic",
    path: "/spray-logs",
    query: { action: "new", historic: "1" },
    roles: ["admin", "manager"],
    managerSection: "sprayLogs",
    labelKey: "sprayLogs.historicMode",
    reader: "src/pages/SprayLogs.tsx",
    hint: "the new application record form with Historic record already switched on (past treatments; by default deducts no stock)",
  },
  {
    id: "spray_logs",
    path: "/spray-logs",
    roles: ["admin", "manager"],
    managerSection: "sprayLogs",
    labelKey: "nav.sprayLogs",
    hint: "application records and their PDF export",
  },

  /* ── Plagas ─────────────────────────────────────────────────────────── */
  {
    id: "pests_add_treatment",
    path: "/pests",
    query: { tab: "treatments", action: "add" },
    roles: ["admin", "manager"],
    managerSection: "pests",
    labelKey: "pests.addTreatment",
    reader: "src/pages/Pests.tsx",
    hint: "the Add treatment (recipe) form",
  },
  {
    id: "pests_catalog",
    path: "/pests",
    query: { tab: "catalog" },
    roles: ["admin", "manager"],
    managerSection: "pests",
    labelKey: "pests.pestListTab",
    reader: "src/pages/Pests.tsx",
    hint: "the pest catalogue, where treatments are linked to each pest",
  },
  {
    id: "pests",
    path: "/pests",
    roles: ["admin", "manager"],
    managerSection: "pests",
    labelKey: "nav.pests",
    hint: "pests and scouting",
  },
  {
    id: "pest_recognition",
    path: "/advanced",
    query: { view: "recognition" },
    roles: ["admin", "manager"],
    managerSection: "advancedFeatures",
    labelKey: "advanced.recognitionTitle",
    reader: "src/pages/AdvancedFeatures.tsx",
    hint: "pest recognition from a photo",
  },
  {
    id: "blocks_map",
    path: "/blocks",
    roles: ["admin", "manager"],
    managerSection: "blockMap",
    labelKey: "nav.blockMap",
    hint: "the map: sectors, pest pressure, the + to log a sampling",
  },

  /* ── Inventario ─────────────────────────────────────────────────────── */
  {
    id: "inventory_add_product",
    path: "/inventory",
    query: { action: "add-product" },
    roles: ["admin", "manager"],
    managerSection: "inventory",
    labelKey: "inventory.addProduct",
    reader: "src/pages/Inventory.tsx",
    hint: "Add product: new product (photo, invoice or by hand) or stock for one you have",
  },
  {
    /* Añadir producto directo en la puerta de la FOTO de la etiqueta, sin la
       pregunta «Producto nuevo / Ya lo tengo»: el escáner ya coteja lo leído con
       el inventario y ofrece reponer el que existe, así que la pregunta no evita
       ningún duplicado aquí. La cámara la abre el toque de la persona (en iOS y en
       el ordenador el selector de archivos exige un gesto): el botón deja la
       puerta delante, no la dispara. */
    id: "inventory_add_product_photo",
    path: "/inventory",
    query: { action: "add-product", mode: "photo" },
    roles: ["admin", "manager"],
    managerSection: "inventory",
    labelKey: "inventory.addByPhoto",
    reader: "src/pages/Inventory.tsx",
    hint: "Add product straight at the label-photo door (one product per photo)",
  },
  {
    /* Lo mismo con la puerta de la FACTURA / albarán (varias líneas a la vez). */
    id: "inventory_add_product_invoice",
    path: "/inventory",
    query: { action: "add-product", mode: "invoice" },
    roles: ["admin", "manager"],
    managerSection: "inventory",
    labelKey: "inventory.scanInvoice",
    reader: "src/pages/Inventory.tsx",
    hint: "Add product straight at the scan-invoice door (photo or PDF of a supplier invoice or delivery note)",
  },
  {
    id: "inventory_product_entry",
    path: "/inventory/:id",
    query: { action: "entry" },
    entity: { kind: "product", param: ":id" },
    roles: ["admin", "manager"],
    managerSection: "inventory",
    labelKey: "inventory.addEntry",
    reader: "src/pages/InventoryDetailPage.tsx",
    hint: "a product's stock entry form (purchase, return)",
  },
  {
    id: "inventory_product_exit",
    path: "/inventory/:id",
    query: { action: "exit" },
    entity: { kind: "product", param: ":id" },
    roles: ["admin", "manager"],
    managerSection: "inventory",
    labelKey: "inventory.addExit",
    reader: "src/pages/InventoryDetailPage.tsx",
    hint: "a product's stock exit form (loss, breakage, recount)",
  },
  {
    id: "inventory_product_edit",
    path: "/inventory/:id",
    query: { action: "edit" },
    entity: { kind: "product", param: ":id" },
    roles: ["admin", "manager"],
    managerSection: "inventory",
    labelKey: "inventory.editProduct",
    reader: "src/pages/InventoryDetailPage.tsx",
    hint: "a product's edit form (price, reorder level, PHI, IRM limit)",
  },
  {
    id: "inventory_product",
    path: "/inventory/:id",
    entity: { kind: "product", param: ":id" },
    roles: ["admin", "manager"],
    managerSection: "inventory",
    labelKey: "nav.inventory",
    hint: "a product's page: stock, warehouses and history",
  },
  {
    id: "inventory",
    path: "/inventory",
    roles: ["admin", "manager"],
    managerSection: "inventory",
    labelKey: "nav.inventory",
    hint: "inventory, and the pending product requests card",
  },
  {
    id: "inventory_consumption",
    path: "/inventory",
    query: { section: "consumption" },
    roles: ["admin", "manager"],
    managerSection: "inventory",
    managerSubPermission: "inventoryConsumption",
    labelKey: "inventory.sectionConsumption",
    reader: "src/pages/Inventory.tsx",
    hint: "consumption notes (product usage as cost)",
  },
  {
    id: "inventory_invoices",
    path: "/inventory/invoices",
    roles: ["admin", "manager"],
    managerSection: "inventory",
    labelKey: "inventoryPaperwork.headerLink",
    hint: "the list of scanned invoices",
  },
  {
    id: "inventory_duplicates",
    path: "/inventory/invoices",
    query: { tab: "duplicates" },
    roles: ["admin"],
    managerSection: "inventory",
    labelKey: "inventoryDuplicates.headerLink",
    reader: "src/pages/InventoryPaperwork.tsx",
    hint: "the duplicate products finder (admin only)",
  },

  /* ── Informes ───────────────────────────────────────────────────────── */
  {
    id: "reports_costs",
    path: "/reports/costes",
    values: COSTS_VALUES,
    valuesTogether: ["date_from", "date_to"],
    queryWhenValues: { preset: "custom" },
    roles: ["admin", "manager"],
    managerSection: "analytics",
    labelKey: "reportsV3.landing.costes.title",
    reader: "src/pages/reports/CostesReport.tsx",
    hint: "the costs report; optional date_from+date_to (both) and mode biological|chemical",
  },
  {
    id: "reports_costs_product",
    path: "/reports/costes",
    entity: { kind: "product", param: "product", value: "key" },
    values: COSTS_VALUES,
    valuesTogether: ["date_from", "date_to"],
    queryWhenValues: { preset: "custom" },
    roles: ["admin", "manager"],
    managerSection: "analytics",
    labelKey: "reportsV3.landing.costes.title",
    reader: "src/pages/reports/CostesReport.tsx",
    hint: "the costs report filtered to ONE product (names.product); same optional dates and mode",
  },
  {
    id: "reports_pests",
    path: "/reports/plagas",
    roles: ["admin", "manager"],
    managerSection: "analytics",
    labelKey: "reportsV3.landing.plagas.title",
    hint: "the pests report",
  },
  {
    id: "analytics",
    path: "/analytics",
    roles: ["admin", "manager"],
    managerSection: "analytics",
    labelKey: "nav.analyticsShort",
    hint: "analytics and the three reports",
  },
  {
    id: "notifications",
    path: "/notifications",
    roles: ["admin", "manager"],
    labelKey: "notifications.title",
    hint: "your notifications",
  },
  {
    id: "shop",
    path: "/shop",
    roles: ["admin", "manager"],
    labelKey: "nav.shop",
    hint: "the supplier catalogue",
  },
];

export const ASSISTANT_ACTION_IDS: string[] = ASSISTANT_ACTIONS.map((a) => a.id);

const BY_ID: Record<string, AssistantAction> = Object.fromEntries(
  ASSISTANT_ACTIONS.map((a) => [a.id, a]),
);

export function getAssistantAction(id: string): AssistantAction | undefined {
  return BY_ID[id];
}

/** Un uuid, en cualquier sitio de un texto. */
export const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** ¿Es una fecha que EXISTE? «2026-02-31» tiene buena forma y no existe. */
export function isRealDate(s: string): boolean {
  if (!DATE_RE.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

/**
 * ¿Valen los valores libres que trae el modelo? null si sí; si no, el motivo,
 * para que la herramienta se lo diga y el modelo PREGUNTE en vez de pintar un
 * botón que la pantalla ignoraría en silencio (el informe de costes, con una
 * sola fecha o una imposible, caía a «últimos 30 días» mientras el asistente
 * decía «puesto en agosto»).
 */
export function invalidActionValues(
  action: AssistantAction,
  values: Record<string, string | null | undefined> = {},
): null | string {
  const given = (arg: string) => values[arg] != null && values[arg] !== "";
  for (const v of action.values ?? []) {
    const raw = values[v.arg];
    if (raw == null || raw === "") continue;
    if (UUID_RE.test(raw)) return `${v.arg}: not a value`;
    if (v.arg === "date_from" || v.arg === "date_to") {
      if (!isRealDate(raw)) return `${v.arg}: not a real date (YYYY-MM-DD)`;
    } else if (v.oneOf && !v.oneOf.includes(raw)) {
      return `${v.arg}: must be one of ${v.oneOf.join(", ")}`;
    }
  }
  const together = action.valuesTogether ?? [];
  if (together.some(given) && !together.every(given)) return `${together.join(" and ")} go together`;
  if (given("date_from") && given("date_to") && String(values.date_from) > String(values.date_to)) {
    return "date_from is after date_to";
  }
  return null;
}

/** Una clave de nombre limpia (la del filtro de producto): texto corto, sin ids ni controles. */
const isCleanKey = (k: string): boolean =>
  k.length > 0 && k.length <= 120 && !UUID_RE.test(k) && !/[\u0000-\u001f\u007f]/.test(k);

/**
 * La URL de una acción. `entityId` / `entityKey` SOLO pueden venir de la
 * resolución del servidor (nunca de los argumentos del modelo); `values` son
 * los libres, validados con `invalidActionValues`. Devuelve null si falta algo
 * o si un valor libre intenta colar un id: mejor ningún botón que uno que abre
 * otra cosa.
 */
export function buildActionUrl(
  action: AssistantAction,
  resolved: {
    entityId?: string | null;
    entityKey?: string | null;
    values?: Record<string, string | null | undefined>;
  } = {},
): string | null {
  if (action.panel) return null;
  let path = action.path;
  const params: Array<[string, string]> = [];

  if (action.entity) {
    if (action.entity.value === "key") {
      const key = resolved.entityKey ?? "";
      if (!isCleanKey(key)) return null;
      params.push([action.entity.param, key]);
    } else {
      const id = resolved.entityId ?? "";
      if (!UUID_RE.test(id) || id.length !== 36) return null;
      if (action.entity.param === ":id") path = path.replace(":id", id);
      else params.push([action.entity.param, id]);
    }
  } else if (path.includes(":")) {
    return null;
  }

  for (const [k, v] of Object.entries(action.query ?? {})) params.push([k, v]);

  if (invalidActionValues(action, resolved.values)) return null;
  let gotDates = false;
  for (const v of action.values ?? []) {
    const raw = resolved.values?.[v.arg];
    if (raw == null || raw === "") continue;
    params.push([v.param, raw]);
    if (v.arg === "date_from" || v.arg === "date_to") gotDates = true;
  }
  if (gotDates) for (const [k, v] of Object.entries(action.queryWhenValues ?? {})) params.push([k, v]);

  const qs = params.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
  return qs ? `${path}?${qs}` : path;
}

/**
 * ¿Es esta URL interna un destino que el asistente puede abrir? El cliente lo
 * comprueba ANTES de navegar un botón: aunque el servidor ya construye las URL,
 * un destino que no está en el registro no se sigue. (Los enlaces que escribe
 * el MODELO en el Markdown pasan por `isAllowedMarkdownUrl`, más estricta.)
 */
export function isAllowedAssistantUrl(url: string): boolean {
  if (typeof url !== "string" || !url.startsWith("/") || url.startsWith("//")) return false;
  if (/[\s\\]/.test(url)) return false;
  const path = url.split(/[?#]/)[0];
  return ASSISTANT_ACTIONS.some((a) => {
    if (a.panel) return false;
    if (!a.path.includes(":id")) return a.path === path;
    const [before, after] = a.path.split(":id");
    if (!path.startsWith(before) || !path.endsWith(after)) return false;
    const id = path.slice(before.length, path.length - after.length);
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  });
}

/**
 * ¿Puede un ENLACE ESCRITO POR EL MODELO en el Markdown llevar aquí? Más
 * estricto que `isAllowedAssistantUrl`, que es para los botones que construye
 * el servidor: el modelo recibe ids de otras herramientas (resolve_entity) y
 * podía escribir `[Cambiar contraseña](/settings/team?edit=<uuid>&step=access)`
 * y saltarse el filtro de rol de open_in_app (el lector ?edit abría el diálogo
 * a un encargado). Aquí solo pasa la página a secas: sin parámetros, sin ids y
 * sin rutas con entidad. Lo concreto, siempre por open_in_app.
 */
export function isAllowedMarkdownUrl(url: string): boolean {
  if (typeof url !== "string" || !url.startsWith("/") || url.startsWith("//")) return false;
  if (/[\s\\?#]/.test(url) || UUID_RE.test(url)) return false;
  return ASSISTANT_ACTIONS.some((a) => !a.panel && !a.entity && !a.path.includes(":") && a.path === url);
}

/**
 * Dominios EXTERNOS que el Markdown del chat puede enlazar: la web y las
 * tiendas de la app, el soporte y las fuentes oficiales del registro. Todo lo
 * demás se pinta como texto: un enlace que el modelo redacte a otro sitio es un
 * canal para sacar datos (o un sitio inventado).
 */
export const ASSISTANT_EXTERNAL_DOMAINS: string[] = [
  "ulturepest.co",
  "ulture.co",
  "wa.me",
  "apps.apple.com",
  "play.google.com",
  "mapa.gob.es",
  "europa.eu",
];

export function isAllowedExternalUrl(url: string): boolean {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return false;
  }
  if (u.protocol !== "https:") return false;
  const host = u.hostname.toLowerCase();
  return ASSISTANT_EXTERNAL_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));
}
